import React, { useCallback, useEffect, useState } from "react";
import {
    getAnalyticsSummary,
    getRecentSearches,
    AnalyticsSummary,
    SearchLogEntry,
} from "../../api/AnalyticsAPI";
import { getUserMessage, logApiError } from "../../api/apiError";

interface Props {
    token: string;
}

const box: React.CSSProperties = {
    border: "1px solid var(--border-color)",
    borderRadius: "var(--radius-md, 6px)",
    padding: "1rem",
    marginBottom: "1rem",
    background: "var(--bg-primary)",
};

/** Compact one-line description of a search's parameter filters. */
function paramsSummary(entry: SearchLogEntry): string {
    const parts: string[] = [];
    (entry.parameters ?? []).forEach((p: any) => {
        const val = typeof p.value === "object" && p.value !== null
            ? JSON.stringify(p.value)
            : String(p.value ?? "");
        const mode = p.mode ? ` ${p.mode}` : "";
        const label = p.name ?? `#${p.id}`; // older entries were logged before name enrichment
        parts.push(`${label}${mode}=${val}`);
    });
    if (entry.coverageChain?.length) parts.push(`coverage:${entry.coverageChain[entry.coverageChain.length - 1]}`);
    if (entry.geoSearch) parts.push("geo");
    return parts.length ? parts.join(", ") : "(no filters)";
}

/** Site analytics: visits, top categories, recorded searches. ANALYTICS_READ only. */
const AnalyticsPanel: React.FC<Props> = ({ token }) => {
    const [days, setDays] = useState(30);
    const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
    const [searches, setSearches] = useState<SearchLogEntry[]>([]);
    const [zeroOnly, setZeroOnly] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [s, rs] = await Promise.all([
                getAnalyticsSummary(token, days),
                getRecentSearches(token, 100, zeroOnly),
            ]);
            setSummary(s);
            setSearches(rs);
        } catch (err) {
            logApiError(err);
            setError(getUserMessage(err));
        } finally {
            setLoading(false);
        }
    }, [token, days, zeroOnly]);

    useEffect(() => { load(); }, [load]);

    const maxDay = Math.max(1, ...(summary?.visitsPerDay ?? []).map((d) => d.count));

    return (
        <div style={{ maxWidth: "60rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1rem" }}>
                <h2 style={{ margin: 0 }}>Analytics</h2>
                <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
                    <option value={7}>Last 7 days</option>
                    <option value={30}>Last 30 days</option>
                    <option value={90}>Last 90 days</option>
                    <option value={180}>Last 180 days</option>
                </select>
                <button type="button" className="btn-sm btn-secondary" onClick={load} disabled={loading}>
                    {loading ? "Loading…" : "Refresh"}
                </button>
            </div>

            {error && <div className="error-message" style={{ marginBottom: "0.75rem" }}>{error}</div>}

            {summary && (
                <>
                    {/* Totals */}
                    <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", marginBottom: "1rem" }}>
                        {[
                            ["Visits", summary.totalVisits],
                            ["Searches", summary.totalSearches],
                            ["Zero-result searches", summary.zeroResultSearches],
                        ].map(([label, value]) => (
                            <div key={String(label)} style={{ ...box, marginBottom: 0, minWidth: "11rem", textAlign: "center" }}>
                                <div style={{ fontSize: "1.6rem", fontWeight: 700 }}>{String(value)}</div>
                                <div style={{ color: "var(--text-secondary)", fontSize: "0.85rem" }}>{label}</div>
                            </div>
                        ))}
                    </div>

                    {/* Visits per day */}
                    <div style={box}>
                        <strong>Visits per day</strong>
                        {summary.visitsPerDay.length === 0 ? (
                            <div style={{ color: "var(--text-secondary)", marginTop: "0.5rem" }}>No visits recorded yet.</div>
                        ) : (
                            <div style={{ marginTop: "0.5rem", display: "flex", flexDirection: "column", gap: "2px" }}>
                                {summary.visitsPerDay.map((d) => (
                                    <div key={d.date} style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.8rem" }}>
                                        <span style={{ width: "6rem", color: "var(--text-secondary)" }}>{d.date}</span>
                                        <div style={{ background: "var(--accent, #2e7d32)", height: "0.8rem", width: `${(d.count / maxDay) * 100}%`, minWidth: "2px", borderRadius: "2px" }} />
                                        <span>{d.count}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Top categories */}
                    <div style={box}>
                        <strong>Top searched categories</strong>
                        {summary.topCategories.length === 0 ? (
                            <div style={{ color: "var(--text-secondary)", marginTop: "0.5rem" }}>No searches recorded yet.</div>
                        ) : (
                            <table style={{ width: "100%", marginTop: "0.5rem", borderCollapse: "collapse" }}>
                                <tbody>
                                    {summary.topCategories.map((c) => (
                                        <tr key={c.categoryId ?? c.categoryName}>
                                            <td style={{ padding: "2px 0" }}>{c.categoryName ?? c.categoryId ?? "(unknown)"}</td>
                                            <td style={{ padding: "2px 0", textAlign: "right", fontWeight: 600 }}>{c.count}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                    {/* Top searched parameters */}
                    <div style={box}>
                        <strong>Top searched parameters</strong>
                        {(summary.topParameters ?? []).length === 0 ? (
                            <div style={{ color: "var(--text-secondary)", marginTop: "0.5rem" }}>
                                No parameter filters recorded yet.
                            </div>
                        ) : (
                            <table style={{ width: "100%", marginTop: "0.5rem", borderCollapse: "collapse" }}>
                                <thead>
                                    <tr style={{ textAlign: "left", fontSize: "0.8rem", color: "var(--text-secondary)" }}>
                                        <th>Category</th>
                                        <th>Parameter</th>
                                        <th style={{ textAlign: "right" }}>Uses</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {summary.topParameters.map((p, i) => (
                                        <tr key={i}>
                                            <td style={{ padding: "2px 6px 2px 0" }}>{p.categoryName ?? "(unknown)"}</td>
                                            <td style={{ padding: "2px 6px 2px 0" }}>{p.parameterName}</td>
                                            <td style={{ padding: "2px 0", textAlign: "right", fontWeight: 600 }}>{p.count}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </>
            )}

            {/* Recent searches */}
            <div style={box}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                    <strong>Recent searches</strong>
                    <label style={{ fontWeight: 400, fontSize: "0.85rem", display: "inline-flex", alignItems: "center", gap: "0.35rem", cursor: "pointer" }}>
                        <input type="checkbox" checked={zeroOnly} onChange={(e) => setZeroOnly(e.target.checked)} />
                        zero-result only
                    </label>
                </div>
                {searches.length === 0 ? (
                    <div style={{ color: "var(--text-secondary)", marginTop: "0.5rem" }}>Nothing recorded{zeroOnly ? " with zero results" : ""} yet.</div>
                ) : (
                    <div className="results-table-wrapper" style={{ marginTop: "0.5rem" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
                            <thead>
                                <tr style={{ textAlign: "left" }}>
                                    <th>When</th>
                                    <th>Category</th>
                                    <th>Filters</th>
                                    <th>Results</th>
                                    <th>User</th>
                                </tr>
                            </thead>
                            <tbody>
                                {searches.map((s, i) => (
                                    <tr key={i} style={{ borderTop: "1px solid var(--border-color)" }}>
                                        <td style={{ padding: "3px 6px 3px 0", whiteSpace: "nowrap" }}>
                                            {s.at ? new Date(s.at).toLocaleString() : ""}
                                        </td>
                                        <td style={{ padding: "3px 6px 3px 0" }}>{s.categoryName ?? s.categoryId}</td>
                                        <td style={{ padding: "3px 6px 3px 0", maxWidth: "22rem", overflow: "hidden", textOverflow: "ellipsis" }}>
                                            {paramsSummary(s)}
                                        </td>
                                        <td style={{ padding: "3px 6px 3px 0", fontWeight: s.resultCount === 0 ? 700 : 400, color: s.resultCount === 0 ? "var(--danger, #b00020)" : undefined }}>
                                            {s.resultCount}
                                        </td>
                                        <td style={{ padding: "3px 0" }}>{s.userId ?? "anon"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AnalyticsPanel;